'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Check,
  X,
  ArrowRight,
  Sparkles,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { trpc } from '@/lib/trpc'

interface PricingContentProps {
  isLoggedIn?: boolean
}

export function PricingContent({ isLoggedIn }: PricingContentProps = {}) {
  const t = useTranslations('pricing')
  const [isAnnual, setIsAnnual] = useState(false)

  const annualSavingsPercent = 17

  const freeFeatures = [
    t('freeFeature1'),
    t('freeFeature2'),
    t('freeFeature3'),
    t('freeFeature4'),
    t('freeFeature5'),
    t('freeFeature6'),
    t('freeFeature7'),
    t('freeFeature8'),
  ]

  const proFeatures = [
    t('proFeature1'),
    t('proFeature2'),
    t('proFeature3'),
    t('proFeature4'),
    t('proFeature5'),
    t('proFeature6'),
    t('proFeature7'),
    t('proFeature8'),
    t('proFeature9'),
  ]

  const { data: pricingFees } = trpc.subscription.getPricingFees.useQuery()

  const formatFee = (feePercent: number, feeFixedCents: number) => {
    const fixedDollars = (feeFixedCents / 100).toFixed(2)
    return `${feePercent}% + $${fixedDollars}`
  }

  const comparisonRows = Array.from({ length: 12 }, (_, i) => {
    const n = i + 1
    // Row 2 (Digital tips) uses dynamic fee data
    if (n === 2 && pricingFees) {
      return {
        feature: t(`compRow${n}` as 'compRow1'),
        free: formatFee(pricingFees.free.feePercent, pricingFees.free.feeFixedCents),
        pro: formatFee(pricingFees.pro.feePercent, pricingFees.pro.feeFixedCents),
      }
    }
    return {
      feature: t(`compRow${n}` as 'compRow1'),
      free: t(`compRow${n}Free` as 'compRow1Free'),
      pro: t(`compRow${n}Pro` as 'compRow1Pro'),
    }
  })

  const faqs = [
    { q: t('faqBillingQ'), a: t('faqBillingA') },
    { q: t('faqCancelQ'), a: t('faqCancelA') },
    { q: t('faqSwitchQ'), a: t('faqSwitchA') },
    { q: t('faqTrialQ'), a: t('faqTrialA') },
    { q: t('faqPaymentQ'), a: t('faqPaymentA') },
    { q: t('faqDowngradeQ'), a: t('faqDowngradeA') },
  ]

  const proPlanParam = isAnnual ? 'pro_annual' : 'pro_monthly'

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/15 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-[128px]" />

        <div className="container mx-auto max-w-4xl text-center relative">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('heroTitle')}
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            {t('heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Billing Toggle + Pricing Cards */}
      <section className="pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Monthly / Annual toggle */}
          <div className="relative flex items-center justify-center gap-3 mb-12">
            <span className={cn('text-sm font-medium transition-colors', !isAnnual ? 'text-white' : 'text-zinc-500')}>
              {t('monthlyLabel')}
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={cn(
                'relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950',
                isAnnual ? 'bg-violet-600' : 'bg-zinc-700'
              )}
              role="switch"
              aria-checked={isAnnual}
              aria-label="Toggle annual billing"
            >
              <span
                className={cn(
                  'inline-block h-5 w-5 transform rounded-full bg-white transition-transform',
                  isAnnual ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
            <span className={cn('text-sm font-medium transition-colors', isAnnual ? 'text-white' : 'text-zinc-500')}>
              {t('annualLabel')}
            </span>
            <span className={cn(
              'absolute left-[calc(50%+5.5rem)] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/30 text-xs font-medium text-green-400 transition-opacity duration-200',
              isAnnual ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}>
              {t('saveBadge', { percent: annualSavingsPercent })}
            </span>
          </div>

          {/* Cards */}
          <div className="grid gap-8 md:grid-cols-2 max-w-3xl mx-auto">
            {/* Free Plan */}
            <div className="flex flex-col p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-1">{t('freePlanName')}</h2>
                <p className="text-sm text-zinc-400">{t('freePlanDescription')}</p>
              </div>

              <div className="mb-8">
                <span className="text-4xl font-bold text-white">{t('freePlanPrice')}</span>
                <span className="text-zinc-500 ml-1">{t('perMonth')}</span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {freeFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link href={isLoggedIn ? '/dashboard' : '/signup'}>
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800"
                >
                  {t('freePlanCta')}
                </Button>
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="flex flex-col p-8 rounded-2xl bg-gradient-to-b from-violet-500/10 to-fuchsia-500/5 border-2 border-violet-500/30 hover:border-violet-500/50 transition-colors relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-xs font-semibold text-white shadow-lg">
                  <Sparkles className="h-3 w-3" />
                  Most Popular
                </span>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-1">{t('proPlanName')}</h2>
                <p className="text-sm text-zinc-400">{t('proPlanDescription')}</p>
              </div>

              <div className="mb-8">
                {isAnnual ? (
                  <>
                    <span className="text-4xl font-bold text-white">{t('proPlanPriceAnnual')}</span>
                    <span className="text-zinc-500 ml-1">{t('perYear')}</span>
                    <p className="text-sm text-zinc-400 mt-1">{t('billedAnnually')}</p>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-white">{t('proPlanPriceMonthly')}</span>
                    <span className="text-zinc-500 ml-1">{t('perMonth')}</span>
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {proFeatures.map((feature, i) => (
                  <li key={feature} className={cn('flex items-start gap-3 text-sm', i === 0 ? 'text-violet-300 font-medium' : 'text-zinc-300')}>
                    {i === 0 ? (
                      <Zap className="h-4 w-4 text-violet-400 mt-0.5 shrink-0" />
                    ) : (
                      <Check className="h-4 w-4 text-violet-400 mt-0.5 shrink-0" />
                    )}
                    {feature}
                  </li>
                ))}
              </ul>

              <Link href={isLoggedIn ? '/select-artist?redirect=subscription' : `/signup?plan=${proPlanParam}`}>
                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all"
                >
                  {t('proPlanCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 px-4 bg-zinc-900/30">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            {t('comparisonTitle')}
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left text-sm font-medium text-zinc-400 pb-4 pr-4">
                    {t('comparisonFeature')}
                  </th>
                  <th className="text-center text-sm font-medium text-zinc-400 pb-4 px-4 w-28">
                    {t('comparisonFree')}
                  </th>
                  <th className="text-center text-sm font-medium text-violet-400 pb-4 pl-4 w-28">
                    {t('comparisonPro')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature} className="border-b border-zinc-800">
                    <td className="py-4 pr-4 text-sm text-zinc-300">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      <ComparisonCell value={row.free} />
                    </td>
                    <td className="py-4 pl-4 text-center">
                      <ComparisonCell value={row.pro} highlight />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-12">
            {t('faqTitle')}
          </h2>

          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-zinc-800 rounded-lg bg-zinc-900/50 px-6"
              >
                <AccordionTrigger className="text-white hover:no-underline text-left">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 via-fuchsia-600/5 to-violet-600/5" />

        <div className="container mx-auto max-w-2xl text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('bottomCtaTitle')}
          </h2>
          <p className="text-lg text-zinc-400 mb-10">
            {t('bottomCtaSubtitle')}
          </p>

          <Link href={isLoggedIn ? '/select-artist?redirect=subscription' : '/signup'}>
            <Button
              size="lg"
              className="text-lg px-8 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all"
            >
              {t('bottomCtaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  )
}

function ComparisonCell({ value, highlight }: { value: string; highlight?: boolean }) {
  if (value === '__yes__') {
    return <Check className={cn('h-5 w-5 mx-auto', highlight ? 'text-violet-400' : 'text-green-400')} />
  }
  if (value === '__no__') {
    return <X className="h-5 w-5 mx-auto text-zinc-600" />
  }
  return (
    <span className={cn('text-sm', highlight ? 'text-violet-300 font-medium' : 'text-zinc-400')}>
      {value}
    </span>
  )
}
